import React from "react";
import { BlogPost } from "@/types/blog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
// import { Card, CardContent } from "@/components/ui/card"; // removed unused
import { Users, Brain, DollarSign, Sparkles, Target } from "lucide-react";

interface BlogNavigationProps {
  posts: BlogPost[];
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
}

// Spezifische Themen-Filter mit präzisen Keywords  
const navigationFilters: Record<string, {
  label: string;
  icon: any;
  description: string;
  color: string;
  categories?: string[];
  keywords?: string[];
}> = {
  all: {
    label: "Alle Artikel",
    icon: Target,
    description: "Gesamte Artikelsammlung durchstöbern",
    color: "bg-gradient-to-r from-primary/5 to-primary/10 text-primary border-primary/30 hover:from-primary/10 hover:to-primary/15"
  },
  
  coaching: {
    label: "Coaching",
    icon: Brain,
    description: "Persönliche Entwicklung & Coaching-Methoden",
    color: "bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-800 border-emerald-300 hover:from-emerald-100 hover:to-teal-100",
    categories: ["Coaching-Philosophie", "Coaching-Methoden"],
    keywords: ["outdoor-coaching", "neuroplastizität", "epigenetik", "coaching-philosophie", "lebensphilosophie", "weisheit", "expertise", "persönliche entwicklung", "haltung", "denkweise"]
  },
  
  finanzen: {
    label: "Finanzen", 
    icon: DollarSign,
    description: "Geldanlage, Börse & Finanzstrategie",
    color: "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-800 border-amber-300 hover:from-amber-100 hover:to-orange-100",
    categories: ["Finanzwissen", "Finanzcoaching"],
    keywords: ["kostolany", "börse", "anlagestrategie", "finanzcoaching", "finanzberatung", "geldanlage", "investment", "gesundes verhältnis"]
  },

  technologie: {
    label: "KI & Tech",
    icon: Sparkles,
    description: "KI, Blockchain & Technologie", 
    color: "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-800 border-blue-300 hover:from-blue-100 hover:to-indigo-100",
    categories: ["Technologie & Systeme"],
    keywords: ["künstliche intelligenz", "webentwicklung", "lovable", "technologie", "digitalisierung", "innovation", "ki-tools", "automatisierung", "krypto", "blockchain", "steuer"]
  },

  psychologie: {
    label: "Psychologie",
    icon: Users,
    description: "Emotionen, Verhalten & soziale Medien",
    color: "bg-gradient-to-r from-purple-50 to-pink-50 text-purple-800 border-purple-300 hover:from-purple-100 hover:to-pink-100",
    categories: ["Finanzpsychologie"],
    keywords: ["musik", "social media", "soundtrack", "neuronale balance", "stimmung", "emotionen manipulieren", "soziale medien", "social-media"]
  },

  fuehrung: {
    label: "Führung",
    icon: Target,
    description: "Executive Coaching & Führungsexzellenz",
    color: "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-800 border-slate-300 hover:from-slate-100 hover:to-gray-100",
    categories: ["Executive Coaching", "Führung"],
    keywords: ["führung", "executive coaching", "leadership", "führungskräfte", "business coaching", "führungsstil", "führungsexzellenz", "digitaler humanismus", "kommunikation", "mindful leadership"]
  }
};

const BlogNavigation: React.FC<BlogNavigationProps> = ({ 
  posts, 
  selectedFilter, 
  onFilterChange 
}) => {
  // Filter-Logik für bessere Zuordnung
  const getFilteredPosts = (filter: string) => {
    if (filter === "all") return posts;
    
    const config = navigationFilters[filter as keyof typeof navigationFilters];
    if (!config) return posts;

    return posts.filter(post => {
      // Suche in Kategorien
      if (config.categories && config.categories.includes(post.category)) {
        return true;
      }
      
      // Suche in Keywords (Title, Excerpt, Tags)
      if (config.keywords) {
        const searchText = `${post.title} ${post.excerpt} ${post.tags?.join(" ") || ""}`.toLowerCase();
        return config.keywords.some(keyword => 
          searchText.includes(keyword.toLowerCase())
        );
      }
      
      return false;
    });
  };

  // Zähle Artikel pro Filter
  const getFilterCount = (filter: string) => {
    return getFilteredPosts(filter).length;
  };

  // Alle verfügbaren Filter
  const allFilters = Object.keys(navigationFilters);

  return (
    <div className="space-y-6 mb-8">

      {/* Filter Buttons - Mobile Optimized Design */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 max-w-7xl mx-auto px-2">
        {allFilters.map((filter) => {
          const config = navigationFilters[filter as keyof typeof navigationFilters];
          const count = getFilterCount(filter);
          const isActive = selectedFilter === filter;
          const Icon = config.icon;

          return (
            <Button
              key={filter}
              variant={isActive ? "default" : "outline"}
              onClick={() => onFilterChange(filter)}
              className={`
                relative min-h-[92px] h-auto sm:min-h-[110px] w-full p-2.5 sm:p-3 py-3 flex flex-col items-center gap-1.5 sm:gap-2
                hover:scale-[1.02] hover:shadow-md transition-all duration-300 group overflow-visible
                ${isActive 
                  ? "ring-2 ring-primary ring-offset-2 shadow-lg" 
                  : `${config.color} border-2 dark:bg-slate-800/50 dark:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700/50`
                }
              `}
            >
              <div className="flex flex-col items-center gap-1.5 sm:gap-2">
                <Icon className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${isActive ? "text-primary-foreground" : "dark:text-slate-200"}`} />
                <span className={`font-semibold text-[13px] sm:text-sm text-center leading-[1.4] break-words hyphens-auto px-0.5 ${isActive ? "text-primary-foreground" : "dark:text-slate-100"}`}>
                  {config.label}
                </span>
                <Badge 
                  variant={isActive ? "secondary" : "default"} 
                  className={`text-xs flex-shrink-0 ${isActive ? "bg-primary-foreground/20" : "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light"}`}
                >
                  {count}
                </Badge>
              </div>
              
            </Button>
          );
        })}
      </div>

      {/* Aktive Filter Info */}
      {selectedFilter !== "all" && (
        <div className="text-center py-2">
          <p className="text-sm text-muted-foreground">
            {getFilterCount(selectedFilter)} Artikel in "{navigationFilters[selectedFilter as keyof typeof navigationFilters]?.label}"
          </p>
        </div>
      )}
    </div>
  );
};

export default BlogNavigation;